import React, { ChangeEvent, useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import clsx from 'clsx';
import { useBucket } from '@context/bucket.context';
import useFormatPrice from '@hooks/use-format-price-hook';
import FormHoc, { FormChildrenProps } from '@components/molecules/form-hoc/form-hoc';
import Button from '@components/atoms/button/button';
import { SuggestedOffers } from '@components/organisms/negotiation/negotiation-modal/suggested-offers/suggested-offers';
import { debounce } from '@helpers/utils/general';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { useNegotiation } from '@context/negotiation.context';
import { useGetPriceDetails } from '@hooks/negotiations/useNegotationQueries';
import Spinner from '@components/atoms/spinner/spinner';
import styles from './negotiation-form.module.scss';

type NegotiationFormProps = {
  isDisabled: boolean;
  action: string;
  currency: string;
  language: string;
  min: number;
  max: number;
  onCancel?: () => void;
  onSubmit: (price: number) => void;
  productId: string;
  isSeller: boolean;
};

type FormValues = {
  offer: number;
};

const NegotiationForm: React.FC<NegotiationFormProps> = ({
  isDisabled = false,
  action,
  currency,
  language,
  min,
  max,
  onSubmit,
  onCancel,
  productId,
  isSeller,
}) => {
  const { t } = useTranslation();
  const { initialData } = useNegotiation();
  const { sendAnalyticEvent } = useAnalyticEvents('mmao');
  const [errorMessage, setErrorMessage] = useState<string>();
  const [debouncePrice, setDebouncePrice] = useState<number>(0);
  const [isLoadingYouEarn, setIsLoadingYouEarn] = useState<boolean>(false);
  const [suggestedOfferIndex, setSuggestedOfferIndex] = useState<number | null>(null);
  const [inputValue, setInputValue] = useState<string>('');
  const formHandlersRef = useRef(null);

  const suggestedOffers = initialData?.suggestedOffers;
  const isValid = !errorMessage;
  const { formatPrice } = useFormatPrice();
  const { data } = useGetPriceDetails(debouncePrice, productId);
  const placeholder = useMemo<string>(() => {
    if (action === 'new') {
      return 'MMAO.POPIN.OTHER_MSG.YOUR_OFFER';
    }
    return 'MMAO.POPIN.OTHER_MSG.YOUR_COUNTER_OFFER';
  }, [isValid, action]);

  const { isFeatureEnabled } = useBucket();
  const isSuggestedOffersEnabled = isFeatureEnabled((f) => f.su3459NegotiationSuggestedOffer, true);
  const isSuggestedOffersShown = isSuggestedOffersEnabled && !isSeller && suggestedOffers && suggestedOffers.length > 0;

  const trackOfferSubmit = () => {
    const offerType = suggestedOfferIndex === null ? 'custom' : 'suggested';
    const suggestedOfferPercent = (suggestedOffers?.[suggestedOfferIndex]?.offerLabel || '').replace('%', '');
    sendAnalyticEvent('mmao_send_an_offer', {
      property: offerType,
      value: suggestedOfferPercent,
    });
  };

  const handleSubmit = async (formValues: FormValues) => {
    if (isValid) {
      onSubmit(formValues.offer);
      trackOfferSubmit();
    }
  };

  const onKeyDown = () => {
    setIsLoadingYouEarn(true);
  };

  const onChangePriceEvent = debounce((e: ChangeEvent<HTMLInputElement>, isValid: boolean) => {
    if (!Boolean(e.target.value) || e.target.value === debouncePrice.toString()) {
      setIsLoadingYouEarn(false);
      return;
    } else if (isValid) {
      setDebouncePrice(Number(e.target.value));
    }
    sendAnalyticEvent('mmao_send_an_offer_error', {
      label: Number(e.target.value) < min ? 'offer_value_too_low' : 'offer_value_too_high',
      property: String(e.target.value),
    });
  }, 500);

  useEffect(() => {
    if (data?.pvn_with_symbol) {
      setIsLoadingYouEarn(false);
    }
  }, [data]);

  const setOfferValue = (offerPrice: number) => {
    if (formHandlersRef && formHandlersRef?.current) {
      const { setFieldValue } = formHandlersRef?.current;
      setFieldValue?.('offer', String(offerPrice));
    }
  };

  const handleSuggestedOfferOnChange = (suggestedOfferIndex: number) => {
    const { setFieldTouched } = formHandlersRef.current;
    setFieldTouched?.('offer', false);
    setInputValue('');
    setErrorMessage('');
    setSuggestedOfferIndex(suggestedOfferIndex);
    setOfferValue(suggestedOffers?.[suggestedOfferIndex]?.offerAmount?.cents / 100);
  };

  const getSendOfferCtaText = (value: number) =>
    t('MMAO.CTA.SEND', {
      amount: value && isValid ? `${formatPrice(value, currency, language)}` : '',
    });

  const getIsSubmitBtnDisabled = (value: number) => !value || !isValid;

  const getErrorMsg = (value: number) => {
    if (value < min) {
      return t('MMAO.POPIN.FORM.LOWER_BOUND.ERROR', {
        amount: formatPrice(min, currency, language),
      });
    } else if (value > max) {
      return t('MMAO.POPIN.FORM.UPPER_BOUND.ERROR', {
        amount: formatPrice(max, currency, language),
      });
    } else {
      return '';
    }
  };

  useEffect(() => {
    // set initial value for suggested offer
    if (initialData?.suggestedOffers) {
      const recommendedIndex = initialData?.suggestedOffers?.findIndex((offer) => offer.recommended);
      setSuggestedOfferIndex(recommendedIndex);
      const initialAmountInCents = initialData?.suggestedOffers?.[recommendedIndex]?.offerAmount?.cents;
      if (initialAmountInCents) {
        setOfferValue(initialAmountInCents / 100);
      }
    }
  }, [initialData]);

  return (
    <>
      {['new', 'offer'].includes(action) && (
        <FormHoc
          initialValues={{
            offer: 0,
          }}
          onSubmit={(data: { offer: number }, { resetForm }) => {
            handleSubmit(data);
            resetForm({
              values: {
                offer: 0,
              },
            });
          }}
        >
          {/* @ts-ignore */}
          {({ setFieldTouched, setFieldValue, handleSubmit, touched, values }: FormChildrenProps<FormValues>) => {
            formHandlersRef.current = {
              setFieldValue,
              setFieldTouched,
            };
            return (
              <form onSubmit={handleSubmit}>
                {isSuggestedOffersShown && (
                  <SuggestedOffers
                    suggestedOffers={initialData?.suggestedOffers}
                    value={suggestedOfferIndex}
                    onChange={handleSuggestedOfferOnChange}
                  />
                )}
                <div
                  className={clsx(
                    `vc-formUnit roundedCorners roundedCorners--withIcon ${styles.mmao__offer__input} ${
                      !isValid && touched.offer ? 'error' : ''
                    }`,
                    isSuggestedOffersShown
                      ? styles['mmao__offer__input--hasSuggestedOffer']
                      : styles['mmao__offer__input--noSuggestedOffer'],
                  )}
                >
                  <input
                    id="offer-price"
                    data-cy="offer_price_input"
                    name="offer"
                    className="vc-inputText"
                    type="number"
                    min={min}
                    max={max}
                    step="1"
                    value={inputValue}
                    placeholder={t(placeholder)}
                    disabled={isDisabled}
                    onKeyDown={(e) => {
                      if (/^[0-9]+$/.test(e.key) || ['Backspace', 'Delete'].includes(e.key)) {
                        onKeyDown();
                      } else if (['ArrowRight', 'ArrowLeft'].includes(e.key)) {
                        return true;
                      } else {
                        e.preventDefault();
                      }
                    }}
                    onChange={(e) => {
                      const val = e.target.value;
                      const msg = touched.offer ? getErrorMsg(Number(val)) : '';
                      setErrorMessage(msg);
                      onChangePriceEvent(e, !Boolean(msg));
                      setInputValue(val || '');
                      setOfferValue(Number(val));
                    }}
                    onFocus={() => {
                      if (!inputValue) {
                        setFieldValue('offer', '');
                        setSuggestedOfferIndex(null);
                      }
                      setFieldTouched('offer');
                    }}
                  />
                  <label className="vc-inputText-label">{t(placeholder)}</label>
                  {isDisabled ? (
                    <span className="vc-inputText-errorMsg">
                      {t('FAVORITE_PAGE.PRODUCT_TILE.MMAO_SHIPPING_DISABLED')}
                    </span>
                  ) : !isValid && touched.offer ? (
                    <span
                      className={`${styles.mmao__offer__input__message} vc-inputText-errorMsg`}
                      data-cy="mmao_error"
                    >
                      {errorMessage}
                    </span>
                  ) : (
                    isSeller && (
                      <div className={styles.mmao__offer__input__message}>
                        {`${t('MMAO.SELLER.YOU_EARN')}: `}
                        {isLoadingYouEarn && values.offer && inputValue ? (
                          <Spinner className={styles.mmao__offer__input__message__spinner} />
                        ) : values.offer && inputValue && data?.pvn_with_symbol ? (
                          data.pvn_with_symbol
                        ) : (
                          '-'
                        )}
                      </div>
                    )
                  )}
                </div>
                <div
                  className={clsx('clearfix', styles.mmao__offer__buttonGroup)}
                  data-cy="mmao_action"
                >
                  <>
                    {action === 'offer' && (
                      <Button
                        variant="secondary"
                        size="large"
                        onClick={() => onCancel?.()}
                      >
                        {t('MMAO.CTA.CANCEL')}
                      </Button>
                    )}
                    <Button
                      type="submit"
                      variant="primary"
                      size="large"
                      fullWidth
                      disabled={getIsSubmitBtnDisabled(values.offer)}
                      iconLeft="send-large"
                    >
                      {getSendOfferCtaText(values.offer)}
                    </Button>
                  </>
                </div>
              </form>
            );
          }}
        </FormHoc>
      )}
    </>
  );
};

export default NegotiationForm;
